import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { ArrowLeft, FileText, DollarSign, Calendar, Camera, Upload } from 'lucide-react';
import { PhotoGalleryModal } from './PhotoGalleryModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const statusConfig = {
  paid: { label: 'PAGADA', bg: 'bg-green-100', text: 'text-green-700', border: 'border-green-200' },
  pending: { label: 'PENDIENTE', bg: 'bg-orange-100', text: 'text-orange-700', border: 'border-orange-200' },
  overdue: { label: 'VENCIDA', bg: 'bg-red-100', text: 'text-red-700', border: 'border-red-200' },
  cancelled: { label: 'CANCELADA', bg: 'bg-gray-100', text: 'text-gray-700', border: 'border-gray-200' },
};

const InvoiceDetail = ({ token, invoiceId, invoiceData, onBack, onNavigate }) => {
  const [invoice, setInvoice] = useState(invoiceData || null);
  const [proofs, setProofs] = useState([]);
  const [galleryOpen, setGalleryOpen] = useState(false);

  const axiosConfig = { headers: { Authorization: `Bearer ${token}` } };

  useEffect(() => {
    if (!invoiceData && invoiceId) {
      axios.get(`${API}/admin/invoices`, axiosConfig).then(res => {
        setInvoice(res.data.find(i => i.id === invoiceId));
      }).catch(() => {});
    }
  }, [invoiceId]);

  useEffect(() => {
    if (!invoice) return;
    axios.get(`${API}/invoices/${invoice.id}/payment-proof`, axiosConfig)
      .then(res => setProofs(res.data || []))
      .catch(() => {});
  }, [invoice]);

  const handleUploadProof = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/jpeg,image/png,image/webp,application/pdf';
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;
      try {
        const fd = new FormData();
        fd.append('file', file);
        await axios.post(`${API}/invoices/${invoice.id}/payment-proof`, fd, {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
        });
        const res = await axios.get(`${API}/invoices/${invoice.id}/payment-proof`, axiosConfig);
        setProofs(res.data || []);
      } catch (err) {
        alert('Error: ' + (err.response?.data?.detail || err.message));
      }
    };
    input.click();
  };

  const [proofThumbs, setProofThumbs] = useState({});
  useEffect(() => {
    const urls = [];
    proofs.forEach(p => {
      if (proofThumbs[p.id] || !p.content_type?.startsWith('image/')) return;
      axios.get(`${API}/files/${p.storage_path}`, { ...axiosConfig, responseType: 'blob' })
        .then(res => {
          const url = URL.createObjectURL(res.data);
          urls.push(url);
          setProofThumbs(prev => ({ ...prev, [p.id]: url }));
        }).catch(() => {});
    });
    return () => urls.forEach(u => URL.revokeObjectURL(u));
  }, [proofs]);

  if (!invoice) return <div className="p-8 text-center text-gray-400">Cargando...</div>;

  const status = statusConfig[invoice.status] || statusConfig.pending;
  const createdDate = invoice.created_at ? new Date(invoice.created_at).toLocaleDateString('es-VE', { year: 'numeric', month: 'long', day: 'numeric' }) : '-';
  const dueDate = invoice.due_date ? new Date(invoice.due_date).toLocaleDateString('es-VE', { year: 'numeric', month: 'long', day: 'numeric' }) : '-';

  return (
    <div className="max-w-5xl mx-auto space-y-6" data-testid="invoice-detail">
      <button onClick={onBack} className="flex items-center gap-2 text-gray-500 hover:text-gray-800 text-sm mb-2">
        <ArrowLeft size={16} /> Volver a Facturas
      </button>

      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 tracking-tight">{invoice.invoice_number}</h1>
          <p className="text-gray-500 mt-1">{invoice.client_name || '-'}</p>
        </div>
        <div className="flex items-center gap-3">
          <span className={`${status.bg} ${status.text} ${status.border} border rounded-full px-4 py-1.5 text-xs font-semibold`}>
            {status.label}
          </span>
          <Button size="sm" variant="outline" onClick={() => window.open(`${API}/admin/invoices/${invoice.id}/pdf`, '_blank')}>
            <FileText size={14} className="mr-1" /> PDF
          </Button>
        </div>
      </div>

      {/* Dates */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <Card className="border border-gray-200 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-xs text-gray-500 mb-1"><Calendar size={14} /> Emisión</div>
            <p className="font-semibold text-gray-900 text-sm">{createdDate}</p>
          </CardContent>
        </Card>
        <Card className="border border-gray-200 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-xs text-gray-500 mb-1"><Calendar size={14} /> Vencimiento</div>
            <p className="font-semibold text-gray-900 text-sm">{dueDate}</p>
          </CardContent>
        </Card>
        <Card className="border border-gray-200 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-xs text-gray-500 mb-1"><FileText size={14} /> WR Asociado</div>
            {invoice.guias?.length > 0 ? (
              <button onClick={() => onNavigate('warehouse', { searchWr: invoice.guias[0] })} className="text-blue-600 hover:underline font-semibold text-sm">
                {invoice.guias[0]}
              </button>
            ) : (
              <p className="text-gray-400 text-sm">Sin WR</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Amounts */}
      <Card className="border border-gray-200 shadow-sm">
        <CardContent className="p-6">
          <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <DollarSign size={18} className="text-green-500" /> Resumen de Montos
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wider">Subtotal</p>
              <p className="text-xl font-semibold text-gray-800">${(invoice.subtotal || 0).toFixed(2)}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wider">Total</p>
              <p className="text-2xl font-bold text-gray-900">${(invoice.total || 0).toFixed(2)}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wider">Pagado</p>
              <p className="text-xl font-semibold text-green-600">${(invoice.paid_amount || 0).toFixed(2)}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wider">Pendiente</p>
              <p className={`text-2xl font-bold ${(invoice.balance_due || 0) > 0 ? 'text-red-600' : 'text-green-600'}`}>
                ${(invoice.balance_due || 0).toFixed(2)}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Line Items */}
      <Card className="border border-gray-200 shadow-sm">
        <CardContent className="p-6">
          <h3 className="font-semibold text-gray-900 mb-4">Detalle de Conceptos</h3>
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b">
                <th className="text-left p-3 font-medium text-gray-600">Descripción</th>
                <th className="text-left p-3 font-medium text-gray-600">Unidad</th>
                <th className="text-right p-3 font-medium text-gray-600">Cantidad</th>
                <th className="text-right p-3 font-medium text-gray-600">Tarifa</th>
                <th className="text-right p-3 font-medium text-gray-600">Monto</th>
              </tr>
            </thead>
            <tbody>
              {(invoice.items || []).map((item, i) => (
                <tr key={i} className="border-b hover:bg-gray-50">
                  <td className="p-3">{item.description}</td>
                  <td className="p-3 text-gray-600">{item.unit}</td>
                  <td className="p-3 text-right">{item.quantity}</td>
                  <td className="p-3 text-right">${(item.rate || 0).toFixed(2)}</td>
                  <td className="p-3 text-right font-semibold">${(item.amount || 0).toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* Comprobantes */}
      <Card className="border border-gray-200 shadow-sm">
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-gray-900 flex items-center gap-2">
              <Camera size={18} className="text-teal-500" /> Comprobantes de Pago ({proofs.length})
            </h3>
            <Button size="sm" variant="outline" onClick={handleUploadProof}>
              <Upload size={14} className="mr-1" /> Subir Comprobante
            </Button>
          </div>
          {proofs.length > 0 ? (
            <div className="grid grid-cols-3 md:grid-cols-5 gap-3">
              {proofs.map(p => (
                <div key={p.id}
                  onClick={() => { setGalleryOpen(true); }}
                  className="aspect-square rounded-lg border border-gray-200 overflow-hidden cursor-pointer hover:ring-2 hover:ring-blue-400 transition-all bg-gray-50 flex items-center justify-center"
                >
                  {proofThumbs[p.id] ? (
                    <img src={proofThumbs[p.id]} alt="Comprobante" className="w-full h-full object-cover" />
                  ) : p.content_type === 'application/pdf' ? (
                    <FileText size={32} className="text-red-400" />
                  ) : (
                    <div className="text-xs text-gray-400 animate-pulse">Cargando...</div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-400 text-sm text-center py-6">No hay comprobantes subidos</p>
          )}
        </CardContent>
      </Card>

      <PhotoGalleryModal isOpen={galleryOpen} onClose={() => setGalleryOpen(false)} files={proofs} token={token} />
    </div>
  );
};

export default InvoiceDetail;
